
import React, { useEffect, useRef, useState } from "react";
import _ from "lodash";
import ErrorTextHelper from "./ErrorTextHelper";
import ReactQuill from "react-quill";
import { Controller } from "react-hook-form";
import "react-quill/dist/quill.snow.css";
import { aiTextToQuillHtml, isLikelyHtml, stripHTMLTags } from "../../utils/helper";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "quill-mention/dist/quill.mention.css";
import "quill-mention/autoregister";
import { getMentioned } from "../../services/feedPage.services";
import { RICH_TEXT_EDITOR_WRAPPER_CLASS } from "./RichTextEditor";
import { patchQuillScrollingContainer } from "../../utils/quillCompat";
import QuillLinkDialog, {
  bindQuillLinkDialog,
  LinkDialogPayload,
} from "./QuillLinkDialog";
import { SnowTheme as EnhancedSnowTheme } from "quill-color-picker-enhance";
import "quill-color-picker-enhance/dist/index.css";
import {
  RICH_TEXT_FORMATS,
  RICH_TEXT_TOOLBAR,
} from "./richTextToolbar";

Quill.register("themes/snow-quill-color-picker-enhance", EnhancedSnowTheme);
((ReactQuill as any).Quill || Quill).register(
  "themes/snow-quill-color-picker-enhance",
  EnhancedSnowTheme
);

// ================================
// Types
// ================================
interface ExternalLabel {
  label?: string;
  className?: string;
  [key: string]: any;
}

type Mention = {
  value: string;
  id: number;
};

interface RichTextEditorProps {
  name: string;
  control?: any;
  externalLabel?: ExternalLabel;
  validationObj?: object;
  showError?: boolean;
  className?: string;
  placeholder?: string;
  showCount?: boolean;
  maxLimit?: number;
  counterAlign?: "left" | "center" | "right";
  useMention?: boolean;
  AIflag?: boolean;
  onAIApplied?: () => void;
  value?: string;
  onChange?: (value: string) => void;
  setOverflow?: (overflow: boolean) => void;
  rules?: object;
  defaultValue?: string;
  onUrlPasted?: (url: string) => void;
  watchUrl?: string | null;
  onUrlRemoved?: () => void;
  [key: string]: any;
}

// ================================
// Quill 2 + color-picker-enhance compat wrapper
// ================================
const EnhancedReactQuill: React.FC<
  React.ComponentProps<typeof ReactQuill> & {
    fieldRef?: React.Ref<HTMLElement | null>;
  }
> = ({ fieldRef, ...props }) => {
  const quillWrapperRef = useRef<ReactQuill>(null);
  const [linkDialog, setLinkDialog] = useState<LinkDialogPayload | null>(null);

  useEffect(() => {
    const quill = quillWrapperRef.current?.getEditor();
    if (!quill) return;

    patchQuillScrollingContainer(quill);
    const unbindLink = bindQuillLinkDialog(quill as any, setLinkDialog);

    if (typeof fieldRef === "function") {
      fieldRef(quill.root);
      return () => {
        unbindLink();
        fieldRef(null);
      };
    }

    return unbindLink;
  }, [fieldRef]);

  return (
    <>
      <ReactQuill
        {...props}
        ref={quillWrapperRef}
        theme={props.theme || "snow-quill-color-picker-enhance"}
      />
      <QuillLinkDialog payload={linkDialog} onClose={() => setLinkDialog(null)} />
    </>
  );
};

// ================================
// QuillMentionField (custom Quill)
// ================================
const QuillMentionField: React.FC<{
  name: string;
  field: any;
  placeholder: string;
  setOverflow?: (overflow: boolean) => void;
  AIflag?: boolean;
  onAIApplied?: () => void;
  externalValue?: string;
  onUrlPasted?: (url: string) => void;
  watchUrl?: string | null;
  onUrlRemoved?: () => void;
}> = ({
  name,
  field,
  placeholder,
  setOverflow,
  AIflag,
  onAIApplied,
  externalValue,
  onUrlPasted,
  watchUrl,
  onUrlRemoved,
}) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const quillRef = useRef<any>(null);
    const fieldRef = useRef(field);
    const [linkDialog, setLinkDialog] = useState<LinkDialogPayload | null>(null);
    const suppressChangeRef = useRef(false);
    const onUrlPastedRef = useRef(onUrlPasted);
    const watchUrlRef = useRef(watchUrl);
    const onUrlRemovedRef = useRef(onUrlRemoved);
    const onAIAppliedRef = useRef(onAIApplied);
    let searchDelay: number | undefined;
    const isPlainUrl = (value: string) => /^https?:\/\/\S+$/i.test(value.trim());

    fieldRef.current = field;
    onUrlPastedRef.current = onUrlPasted;
    watchUrlRef.current = watchUrl;
    onUrlRemovedRef.current = onUrlRemoved;
    onAIAppliedRef.current = onAIApplied;

    const fetchMentionValues = (search: string): Promise<Mention[]> =>
      new Promise((resolve) => {
        if (searchDelay) window.clearTimeout(searchDelay);
        searchDelay = window.setTimeout(async () => {
          try {
            const apiData: any = await getMentioned(search);
            const responseData = _.get(apiData, "data.data", []) as Mention[];
            resolve(responseData);
          } catch (error) {
            console.error("Error fetching mention values:", error);
            resolve([]);
          }
        }, 500);
      });

    useEffect(() => {
      if (!editorRef.current || quillRef.current) return;

      const quill = new Quill(editorRef.current, {
        theme: "snow-quill-color-picker-enhance", // <-- use enhanced theme
        placeholder,
        modules: {
          toolbar: RICH_TEXT_TOOLBAR,
          mention: {
            allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
            mentionDenotationChars: ["@"],
            dataAttributes: ["id", "value", "slug", "is_company"],
            showDenotationChar: true,
            positioningStrategy: "fixed",
            onSelect: (item: Record<string, string | undefined>, insertItem: (data: Record<string, unknown>) => void) => {
              const data = {
                ...item,
                id: item.id != null ? String(item.id) : undefined,
                value: item.value,
                slug: item.slug != null ? String(item.slug) : undefined,
                is_company: item.is_company ?? item.isCompany,
              };
              insertItem(data);
            },
            source: async (
              searchTerm: string,
              renderList: (items: Mention[], term: string) => void,
              mentionChar: string
            ) => {
              try {
                if (mentionChar === "@") {
                  const data = await fetchMentionValues(searchTerm || "");
                  renderList(data, searchTerm);
                  setOverflow?.(true);
                  return;
                }
                renderList([], searchTerm);
              } catch (e) {
                console.error(e);
                renderList([], searchTerm);
              }
            },
          },
        },
      });

      quillRef.current = quill;
      quill.root.setAttribute("autocapitalize", "off");
      quill.root.setAttribute("autocorrect", "off");
      patchQuillScrollingContainer(quill);
      const unbindLink = bindQuillLinkDialog(quill as any, setLinkDialog);
      fieldRef.current.ref(quill.root);

      // initial value
      if (fieldRef.current?.value && fieldRef.current.value !== "<p><br></p>") {
        suppressChangeRef.current = true;
        quill.clipboard.dangerouslyPasteHTML(fieldRef.current.value);
        suppressChangeRef.current = false;
      }

      const handleTextChange = () => {
        if (suppressChangeRef.current) return;
        requestAnimationFrame(() => {
          const html = quill.root.innerHTML;
          if (fieldRef.current) {
            fieldRef.current.onChange(html);
          }
          setOverflow?.(false);

          if (watchUrlRef.current && !quill.getText().includes(watchUrlRef.current)) {
            onUrlRemovedRef.current?.();
          }
        });
      };

      quill.on("text-change", handleTextChange);

      const handlePaste = (event: ClipboardEvent) => {
        const pastedText = event.clipboardData?.getData("text/plain")?.trim() || "";

        // Some browsers include text/html even for plain URL paste.
        // If plain text itself is a URL, force insert as a link.
        if (!pastedText || !isPlainUrl(pastedText)) return;

        event.preventDefault();
        event.stopPropagation();
        const selection = quill.getSelection(true);
        const index = selection?.index ?? quill.getLength();

        quill.insertText(index, pastedText, "link", pastedText, "user");
        quill.setSelection(index + pastedText.length, 0, "silent");

        onUrlPastedRef.current?.(pastedText);
      };

      quill.root.addEventListener("paste", handlePaste, true);

      return () => {
        unbindLink();
        fieldRef.current?.ref(null);
        if (quillRef.current) {
          quillRef.current.off("text-change", handleTextChange);
          quill.root.removeEventListener("paste", handlePaste, true);
          quillRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placeholder]);

    // Apply AI HTML once when INSERT TO POST flips AIflag on.
    useEffect(() => {
      const quill = quillRef.current;
      if (!quill || !AIflag || externalValue === undefined) return;

      const html = isLikelyHtml(externalValue)
        ? externalValue
        : aiTextToQuillHtml(externalValue);

      suppressChangeRef.current = true;
      const converted = quill.clipboard.convert({ html: html || "<p><br></p>" });
      quill.setContents(converted, "silent");

      const expectedParas = (html.match(/<p\b[^>]*>[\s\S]*?<\/p>/gi) || [])
        .map((block) => stripHTMLTags(block).replace(/[\u200B-\u200D\uFEFF]/g, "").trim())
        .filter(Boolean);
      const actualParas = quill
        .getText()
        .split(/\n+/)
        .map((part: string) => part.trim())
        .filter(Boolean);

      if (expectedParas.length > 1 && actualParas.length <= 1) {
        const ops = expectedParas.flatMap((para, index) => [
          { insert: para.replace(/\n/g, " ") },
          { insert: index < expectedParas.length - 1 ? "\n\n" : "\n" },
        ]);
        quill.setContents(ops, "silent");
      }

      const resultHtml = quill.root.innerHTML;
      suppressChangeRef.current = false;
      fieldRef.current?.onChange(resultHtml);
      onAIAppliedRef.current?.();
      // Only run when AIflag turns on; postText then updates from onChange.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [AIflag]);

    return (
      <div className="quill">
        <div ref={editorRef} />
        <QuillLinkDialog payload={linkDialog} onClose={() => setLinkDialog(null)} />
      </div>
    );
  };

// ================================
// Main component
// ================================
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  name,
  control,
  validationObj,
  className = "",
  placeholder = "",
  showError = true,
  showCount = false,
  maxLimit = 1000,
  counterAlign = "right",
  useMention = false,
  AIflag = false,
  onAIApplied,
  externalLabel,
  ...props
}) => {
  const labelText = externalLabel?.label || "";
  const labelClassName = externalLabel?.className || "";
  const externalLabelClasses = _.chain([labelClassName])
    .uniq()
    .join(" ")
    .split(" ")
    .uniq()
    .join(" ")
    .trim()
    .value();

  const errorMessage = _.get(validationObj, `${name}.message`, null);

  const isEmptyContent = (content: string): boolean => {
    const initValues = [
      "<p><br></p>",
      "<p><br></p><p><br></p>",
      "<h1><br></h1>",
      "<h2><br></h2>",
      "<h3><br></h3>",
      "<ol><li><br></li></ol>",
      "<ul><li><br></li></ul>",
    ];
    return initValues.some((val) => val.trim() === content.trim());
  };

  const handleOnChange = (content: string) => {
    if (typeof props.onChange === "function") {
      props.onChange(content);
    }
  };

  const renderEditor = () => {
    if (control) {
      return (
        <Controller
          name={name}
          control={control}
          rules={props.rules || {}}
          defaultValue={props.defaultValue || ""}
          render={({ field }) => (
            <>
              {useMention ? (
                <QuillMentionField
                  key={name}
                  name={name}
                  field={field}
                  placeholder={placeholder}
                  setOverflow={props.setOverflow}
                  AIflag={AIflag}
                  onAIApplied={onAIApplied}
                  externalValue={props.value}
                  onUrlPasted={props.onUrlPasted}
                  watchUrl={props.watchUrl}
                  onUrlRemoved={props.onUrlRemoved}
                />
              ) : (
                <EnhancedReactQuill
                  fieldRef={field.ref}
                  value={field.value || ""}
                  placeholder={placeholder}
                  onChange={(content: string) => {
                    if (isEmptyContent(content)) {
                      field.onChange("");
                    } else {
                      field.onChange(content);
                    }
                  }}
                  modules={{ toolbar: RICH_TEXT_TOOLBAR }}
                  formats={RICH_TEXT_FORMATS}
                  theme="snow-quill-color-picker-enhance" // <-- use enhanced theme
                />
              )}

              {showCount && (
                <p
                  className={`${className} mt-2 global-counter ${(counterAlign === "center" || counterAlign === "right") &&
                    `align-${counterAlign}`
                    } ${stripHTMLTags(field.value || "").length > maxLimit &&
                    "error-text"
                    }`}
                >
                  {`${stripHTMLTags(field.value || "").length}/${maxLimit}`}
                </p>
              )}
            </>
          )}
        />
      );
    }

    // uncontrolled / external-state usage
    return (
      <EnhancedReactQuill
        value={props.value || ""}
        placeholder={placeholder}
        onChange={(content: string) => {
          if (isEmptyContent(content)) {
            handleOnChange("");
          } else {
            handleOnChange(content);
          }
        }}
        modules={{ toolbar: RICH_TEXT_TOOLBAR }}
        formats={RICH_TEXT_FORMATS}
        theme="snow-quill-color-picker-enhance" // <-- use enhanced theme
      />
    );
  };

  return (
    <>
      {labelText && (
        <label htmlFor={name} className={externalLabelClasses}>
          {labelText}
        </label>
      )}

      <div className={RICH_TEXT_EDITOR_WRAPPER_CLASS}>
        {renderEditor()}
      </div>

      {errorMessage && showError && <ErrorTextHelper text={errorMessage} />}
    </>
  );
};

export default RichTextEditor;
